import { useEffect, useState, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../services/supabase'

interface OrderItem {
  id: string
  product_id: string
  variant_id: string | null
  variant_name: string | null
  variant_value: string | null
  quantity: number
  price_at_purchase: number
  product?: {
    name: string
    image_urls: string[]
  }
}

interface ShippingAddress {
  full_name: string
  street_address: string
  city: string
  state: string
  postal_code: string
  country: string
  phone: string
  email?: string
  subtotal?: number
  shipping_cost?: number
  tax_amount?: number
}

interface Order {
  id: string
  customer_id: string
  total_amount: number
  status: string
  payment_intent_id: string | null
  shipping_address: ShippingAddress
  created_at: string
  updated_at: string
  items?: OrderItem[]
}

type StatusFilter = 'all' | 'pending' | 'processing' | 'shipped' | 'delivered'

const statusColors: Record<string, { bg: string; text: string }> = {
  pending: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  processing: { bg: 'bg-blue-100', text: 'text-blue-800' },
  shipped: { bg: 'bg-purple-100', text: 'text-purple-800' },
  delivered: { bg: 'bg-green-100', text: 'text-green-800' },
  cancelled: { bg: 'bg-red-100', text: 'text-red-800' },
}

const statusFilters: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All Orders' },
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'delivered', label: 'Delivered' },
]

export default function Orders() {
  const { user, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null)

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', { replace: true })
    }
  }, [user, authLoading, navigate])

  // Fetch orders with items for the logged-in user
  useEffect(() => {
    async function fetchOrdersWithItems() {
      if (!user) return

      setLoading(true)
      setError(null)

      try {
        // Fetch orders
        const { data: ordersData, error: ordersError } = await supabase
          .from('orders')
          .select('*')
          .eq('customer_id', user.id)
          .order('created_at', { ascending: false })

        if (ordersError) throw ordersError

        if (!ordersData || ordersData.length === 0) {
          setOrders([])
          return
        }

        // Fetch all order items with product info for search functionality
        const orderIds = ordersData.map(o => o.id)
        const { data: itemsData, error: itemsError } = await supabase
          .from('order_items')
          .select(`
            id,
            order_id,
            product_id,
            variant_id,
            variant_name,
            variant_value,
            quantity,
            price_at_purchase,
            product:products(name, image_urls)
          `)
          .in('order_id', orderIds)

        if (itemsError) throw itemsError

        // Combine orders with their items
        const ordersWithItems = ordersData.map(order => ({
          ...order,
          items: (itemsData || [])
            .filter(item => item.order_id === order.id)
            .map(item => ({
              id: item.id,
              product_id: item.product_id,
              variant_id: item.variant_id,
              variant_name: item.variant_name,
              variant_value: item.variant_value,
              quantity: item.quantity,
              price_at_purchase: item.price_at_purchase,
              product: (Array.isArray(item.product) ? item.product[0] : item.product) as { name: string; image_urls: string[] } | undefined
            }))
        }))

        setOrders(ordersWithItems)
      } catch (err) {
        console.error('Error fetching orders:', err)
        setError('Failed to load orders. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    if (user) {
      fetchOrdersWithItems()
    }
  }, [user])

  // Filter orders based on search query and status filter
  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      // Status filter
      if (statusFilter !== 'all' && order.status !== statusFilter) {
        return false
      }

      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim()

        // Check order ID
        const orderIdMatch = order.id.toLowerCase().includes(query)

        // Check product names in order items
        const productNameMatch = order.items?.some(item =>
          item.product?.name?.toLowerCase().includes(query)
        )

        if (!orderIdMatch && !productNameMatch) {
          return false
        }
      }

      return true
    })
  }, [orders, searchQuery, statusFilter])

  // Toggle order expansion
  const toggleOrder = (orderId: string) => {
    if (expandedOrderId === orderId) {
      setExpandedOrderId(null)
    } else {
      setExpandedOrderId(orderId)
    }
  }

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  // Format order ID for display
  const formatOrderId = (id: string) => {
    return id.substring(0, 8).toUpperCase()
  }

  // Clear filters
  const clearFilters = () => {
    setSearchQuery('')
    setStatusFilter('all')
  }

  // Check if any filters are active
  const hasActiveFilters = searchQuery.trim() !== '' || statusFilter !== 'all'

  // Loading state
  if (authLoading || loading) {
    return (
      <div className="min-h-screen">
        <div className="max-w-4xl mx-auto px-6 py-10">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Order History</h1>
            <p className="text-gray-500 text-sm">View your past orders</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-16 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-purple-600 mx-auto"></div>
            <p className="mt-4 text-gray-500 text-sm">Loading orders...</p>
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen">
        <div className="max-w-4xl mx-auto px-6 py-10">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Order History</h1>
            <p className="text-gray-500 text-sm">View your past orders</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-6 h-6 text-red-600"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                />
              </svg>
            </div>
            <p className="text-gray-700 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-4xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-4 h-4 mr-1"
              style={{ width: '16px', height: '16px' }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
            Back to Home
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Order History</h1>
          <p className="text-gray-500 text-sm">View and track your past orders</p>
        </div>

        {/* No Orders Empty State */}
        {orders.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-8 h-8 text-gray-400"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">No orders yet</h2>
            <p className="text-gray-500 mb-6">You haven't placed any orders yet.</p>
            <Link
              to="/products"
              className="inline-flex items-center px-6 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
            >
              Start Shopping
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="w-4 h-4 ml-2"
                style={{ width: '16px', height: '16px' }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
            </Link>
          </div>
        ) : (
          <>
            {/* Search and Filter Section */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-6">
              {/* Search Bar */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-5 h-5 text-gray-400"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search by order number or product name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-5 h-5 text-gray-400 hover:text-gray-600"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Status Filter Buttons */}
              <div className="flex flex-wrap gap-2 mt-4">
                {statusFilters.map((filter) => (
                  <button
                    key={filter.value}
                    onClick={() => setStatusFilter(filter.value)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      statusFilter === filter.value
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Results Count */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-500">
                {filteredOrders.length === orders.length
                  ? `${orders.length} order${orders.length !== 1 ? 's' : ''}`
                  : `${filteredOrders.length} of ${orders.length} orders`}
              </p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                >
                  Clear filters
                </button>
              )}
            </div>

            {/* No Results Empty State */}
            {filteredOrders.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-8 h-8 text-gray-400"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                    />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-gray-900 mb-2">No orders match your search</h2>
                <p className="text-gray-500 mb-6">
                  Try adjusting your search or filter to find what you're looking for.
                </p>
                <button
                  onClick={clearFilters}
                  className="px-6 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              /* Orders List */
              <div className="space-y-4">
                {filteredOrders.map((order) => {
                  const isExpanded = expandedOrderId === order.id
                  const statusStyle = statusColors[order.status] || statusColors.pending

                  return (
                    <div
                      key={order.id}
                      className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
                    >
                      {/* Order Header - Clickable */}
                      <button
                        onClick={() => toggleOrder(order.id)}
                        className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          {/* Order Icon */}
                          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="w-5 h-5 text-purple-600"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                              />
                            </svg>
                          </div>

                          {/* Order Info */}
                          <div className="text-left">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-gray-900">
                                Order #{formatOrderId(order.id)}
                              </span>
                              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusStyle.bg} ${statusStyle.text}`}>
                                {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500 mt-0.5">
                              {formatDate(order.created_at)}
                            </p>
                          </div>
                        </div>

                        {/* Right Side */}
                        <div className="flex items-center gap-4">
                          <span className="font-semibold text-gray-900">
                            ${order.total_amount.toFixed(2)}
                          </span>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={2}
                            stroke="currentColor"
                            className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                            style={{ width: '20px', height: '20px' }}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                          </svg>
                        </div>
                      </button>

                      {/* Expanded Order Details */}
                      {isExpanded && (
                        <div className="border-t border-gray-100 px-5 py-4 bg-gray-50">
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Order Items */}
                            <div>
                              <h3 className="text-sm font-semibold text-gray-700 mb-3">Items</h3>
                              <div className="space-y-3">
                                {order.items?.map((item) => (
                                  <div key={item.id} className="flex items-center gap-3 bg-white rounded-lg p-3 border border-gray-100">
                                    {/* Product Image */}
                                    <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                                      {item.product?.image_urls?.[0] ? (
                                        <img
                                          src={item.product.image_urls[0]}
                                          alt={item.product.name}
                                          className="w-full h-full object-contain"
                                        />
                                      ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            strokeWidth={1}
                                            stroke="currentColor"
                                            className="w-5 h-5 text-gray-300"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                                            />
                                          </svg>
                                        </div>
                                      )}
                                    </div>

                                    {/* Product Details */}
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-gray-900 truncate">
                                        {item.product?.name || 'Unknown Product'}
                                      </p>
                                      {item.variant_name && item.variant_value && (
                                        <p className="text-xs text-purple-600">
                                          {item.variant_name}: {item.variant_value}
                                        </p>
                                      )}
                                      <p className="text-xs text-gray-500">
                                        Qty: {item.quantity} × ${item.price_at_purchase.toFixed(2)}
                                      </p>
                                    </div>

                                    {/* Item Total */}
                                    <span className="text-sm font-medium text-gray-900">
                                      ${(item.quantity * item.price_at_purchase).toFixed(2)}
                                    </span>
                                  </div>
                                ))}
                              </div>

                              {/* Order Totals */}
                              <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-500">Subtotal</span>
                                  <span className="text-gray-900">
                                    ${(order.shipping_address?.subtotal || 0).toFixed(2)}
                                  </span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-500">Shipping</span>
                                  <span className="text-gray-900">
                                    {(order.shipping_address?.shipping_cost || 0) === 0
                                      ? 'FREE'
                                      : `$${(order.shipping_address?.shipping_cost || 0).toFixed(2)}`}
                                  </span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-500">Tax</span>
                                  <span className="text-gray-900">
                                    ${(order.shipping_address?.tax_amount || 0).toFixed(2)}
                                  </span>
                                </div>
                                <div className="flex justify-between text-sm font-semibold pt-2 border-t border-gray-200">
                                  <span className="text-gray-900">Total</span>
                                  <span className="text-gray-900">${order.total_amount.toFixed(2)}</span>
                                </div>
                              </div>
                            </div>

                            {/* Shipping Address */}
                            <div>
                              <h3 className="text-sm font-semibold text-gray-700 mb-3">Shipping Address</h3>
                              <div className="bg-white rounded-lg p-4 border border-gray-100">
                                <p className="font-medium text-gray-900">
                                  {order.shipping_address?.full_name}
                                </p>
                                <p className="text-sm text-gray-600 mt-1">
                                  {order.shipping_address?.street_address}
                                </p>
                                <p className="text-sm text-gray-600">
                                  {order.shipping_address?.city}, {order.shipping_address?.state} {order.shipping_address?.postal_code}
                                </p>
                                <p className="text-sm text-gray-600">
                                  {order.shipping_address?.country}
                                </p>
                                {order.shipping_address?.phone && (
                                  <p className="text-sm text-gray-500 mt-2">
                                    Phone: {order.shipping_address.phone}
                                  </p>
                                )}
                              </div>

                              {/* Order Info */}
                              <h3 className="text-sm font-semibold text-gray-700 mb-3 mt-4">Order Information</h3>
                              <div className="bg-white rounded-lg p-4 border border-gray-100 space-y-2">
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-500">Order ID</span>
                                  <span className="text-gray-900 font-mono text-xs">
                                    {order.id}
                                  </span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-500">Placed on</span>
                                  <span className="text-gray-900">
                                    {formatDate(order.created_at)}
                                  </span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-500">Status</span>
                                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusStyle.bg} ${statusStyle.text}`}>
                                    {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
